import React, { memo, useCallback } from 'react'
import { AuthKitProvider } from '@workos-inc/authkit-react'
import { useNavigate } from 'react-router-dom'
import { IS_PRODUCTION } from 'src/constants/environment'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { WORKOS_API_HOSTNAME, WORKOS_CLIENT_ID } from 'src/constants/workos'
import { readReturnToPath } from 'src/utils/workosReturnTo'
import { MissingWorkosConfig } from './MissingWorkosConfig'
import { WorkosAuthBridge } from './WorkosAuthBridge'

interface Props {
  children: React.ReactNode
}

/**
 * Rendered inside the router so the post-sign-in hop can use `useNavigate` rather than a reload.
 *
 * AuthKit sends the browser back to the app origin, not to the page the user was on, so the
 * originally requested path rides along in the OAuth `state` and is restored here — see
 * `readReturnToPath` for why that value has to be validated before it is used.
 */
const WorkosAuthProviderComponent = ({ children }: Props) => {
  const navigate = useNavigate()

  const handleRedirectCallback = useCallback(
    ({ state }: { state: Record<string, any> | null | undefined }) => {
      const returnToPath = readReturnToPath(state, window.location.origin)

      navigate(returnToPath ?? ADMIN_ROUTES.LAUNCHER_URL, { replace: true })
    },
    [navigate],
  )

  if (!WORKOS_CLIENT_ID) {
    return <MissingWorkosConfig />
  }

  return (
    <AuthKitProvider
      clientId={WORKOS_CLIENT_ID}
      apiHostname={WORKOS_API_HOSTNAME}
      // Off in production only. AuthKit keeps the refresh token in a cookie on `apiHostname`,
      // which the browser sends only when the app and the WorkOS auth domain are same-site.
      // Production serves the app from `internal.gigsafe.com` and AuthKit from
      // `auth.internal.gigsafe.com`, so the cookie works there. Non-production builds are still
      // served from `*.web.app`, where that cookie is third-party and never arrives, so they keep
      // `devMode`: the refresh token lives in localStorage and rides in the request body instead,
      // which works anywhere at the cost of exposing it to XSS. See ENG-981.
      devMode={!IS_PRODUCTION}
      onRedirectCallback={handleRedirectCallback}
    >
      <WorkosAuthBridge>{children}</WorkosAuthBridge>
    </AuthKitProvider>
  )
}

export const WorkosAuthProvider = memo(WorkosAuthProviderComponent)
